import { useEffect, useState } from 'react';
import Modal from '../Modal';
import complianceService from '../../services/compliance.service';
import portfolioService from '../../services/portfolio.service';
import { formatMoney } from '../../lib/datetime';
import { cleanMfaInput, stepUpErrorMessage } from '../../lib/mfaApproval';
import { colors } from '../../styles/theme';
import type { ComplianceState } from '../../types/compliance';
import type { ProductBalance } from '../../types/finance';
import { DialogFooter, ErrorBox, RecordRow, RowButton } from './ClientRecordsControls';
import { canGiveEstateExit, estateExitBlockedReason, exitableHoldings } from './estateExit.helpers';

/**
 * The estate's exit at a term end (owner, 2026-11-03, S-61 amended), on the client compliance
 * panel. A holding of a product held to the end of its term, whose holder has died, exits at the
 * next term end, and only staff give that exit once the death is confirmed: the notice not to
 * renew the whole holding, on the holder's behalf (or the holder's own notice, adopted). A second
 * manager approves it on the redemption queue; its payment is held until the estate can be
 * settled. Shown only when the API would take it.
 */
export default function EstateTermEndExit({
  clientId, state, onDone,
}: Readonly<{
  clientId: string;
  state: ComplianceState | null;
  onDone: (msg: string) => void;
}>): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!state || !canGiveEstateExit(state) || !state.succession_case_id) return null;
  const caseId = state.succession_case_id;

  return (
    <RecordRow
      label="Exit at the term end for the estate"
      detail="The death is confirmed. A holding held to the end of its term renews until its exit is given; its payment is held until the estate can be settled."
    >
      <RowButton label="Give the exit" onClick={() => setOpen(true)} />
      {open && (
        <ExitModal
          clientId={clientId}
          caseId={caseId}
          onClose={() => setOpen(false)}
          onDone={(msg) => {
            setOpen(false);
            onDone(msg);
          }}
        />
      )}
    </RecordRow>
  );
}

function ExitModal({
  clientId, caseId, onClose, onDone,
}: Readonly<{
  clientId: string;
  caseId: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}>): JSX.Element {
  const [holdings, setHoldings] = useState<ProductBalance[] | null>(null);
  const [productId, setProductId] = useState('');
  const [reason, setReason] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    portfolioService
      .getClientBalance(clientId)
      .then((res) => {
        if (cancelled) return;
        const found = exitableHoldings(res.items);
        setHoldings(found);
        setProductId(found[0]?.product_id ?? '');
      })
      .catch(() => {
        if (!cancelled) setErr('Could not load the holdings. Close and try again.');
      });
    return () => { cancelled = true; };
  }, [clientId]);

  const blocked = estateExitBlockedReason(productId, reason, code);

  const submit = async (): Promise<void> => {
    if (blocked !== null || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const given = await complianceService.giveEstateTermEndExit(
        caseId, productId, reason.trim(), code,
      );
      onDone(given.adopted
        ? "The holder's own notice not to renew is now the estate's exit. A second manager approves it."
        : 'The exit at the term end is given for the estate. A second manager approves it.');
    } catch (e: unknown) {
      setErr(stepUpErrorMessage(e, 'Could not give the exit.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Exit at the term end for the estate
      </h2>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        Gives the notice not to renew the whole holding, on the holder&apos;s behalf. It is paid at
        the end of the term funds names, to the estate, once the estate can be settled; nothing is
        paid before then.
      </p>

      {holdings !== null && holdings.length === 0 && (
        <p className="text-sm mb-4" style={{ color: colors.muted }}>
          This client has no holding held to the end of its term.
        </p>
      )}
      {holdings !== null && holdings.length > 0 && (
        <>
          <label htmlFor="estate-exit-product" className="block text-xs font-medium mb-1"
                 style={{ color: colors.muted }}>
            Holding
          </label>
          <select
            id="estate-exit-product"
            value={productId}
            onChange={(e) => setProductId(e.target.value)}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={{ border: `1px solid ${colors.border}`, background: colors.surface }}
          >
            {holdings.map((h) => (
              <option key={h.product_id} value={h.product_id}>
                {h.product_name} · {formatMoney(h.balance, h.currency_code)}
              </option>
            ))}
          </select>
        </>
      )}

      <label htmlFor="estate-exit-reason" className="block text-xs font-medium mb-1"
             style={{ color: colors.muted }}>
        Reason (kept in the audit log)
      </label>
      <textarea
        id="estate-exit-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        maxLength={2000}
        className="w-full px-3 py-2 rounded-lg text-sm mb-3"
        style={{ border: `1px solid ${colors.border}` }}
      />

      <label htmlFor="estate-exit-code" className="block text-xs font-medium mb-1"
             style={{ color: colors.muted }}>
        Code from your authenticator app
      </label>
      <input
        id="estate-exit-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={code}
        onChange={(e) => setCode(cleanMfaInput(e.target.value))}
        className="w-full px-3 py-2 rounded-lg text-sm font-mono tracking-widest mb-4"
        style={{ border: `1px solid ${colors.border}` }}
      />

      <ErrorBox message={err} />
      <DialogFooter
        busy={busy}
        blocked={blocked}
        cta="Give the exit"
        onClose={onClose}
        onSubmit={submit}
      />
    </Modal>
  );
}
